import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { GoogleSignInButton, GoogleSignInButtonCustom } from './GoogleSignInButton';
import { ForgotPasswordModal } from './Auth/ForgotPasswordModal';
import { TurnstileWidget, turnstileEnabled } from './Auth/TurnstileWidget';

interface EducatorLoginPageProps {
    onLogin: (user: any) => void;
}

export const EducatorLoginPage: React.FC<EducatorLoginPageProps> = ({ onLogin }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [mode, setMode] = useState<'login' | 'register'>('login');
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [selectedRole, setSelectedRole] = useState<'teacher' | 'consultant'>('teacher');
    const [branch, setBranch] = useState('Matematik');
    const [success, setSuccess] = useState('');
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [turnstileToken, setTurnstileToken] = useState<string>('');
    // Jeton tek kullanımlık: başarısız denemeden sonra sıfırlanmazsa ikinci
    // deneme harcanmış jetonla gidiyor ve "robot doğrulaması başarısız" alıyor.
    const [turnstileSifirla, setTurnstileSifirla] = useState(0);
    const turnstileTazele = () => { setTurnstileToken(''); setTurnstileSifirla((n) => n + 1); };

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        if (turnstileEnabled && !turnstileToken) {
            setError('Lütfen robot olmadığınızı doğrulayın.');
            return;
        }
        setLoading(true);

        try {
            const user = await api.login(email, password, undefined, undefined, undefined, turnstileToken || undefined);
            if (user) {
                // Only allow teachers and consultants
                if (user?.role !== 'teacher' && user?.role !== 'education_consultant') {
                    setError('Bu sayfa sadece öğretmen ve danışmanlar içindir.');
                    turnstileTazele();
                    setLoading(false);
                    return;
                }

                // Validate selected role matches actual role
                const expectedRole = selectedRole === 'teacher' ? 'teacher' : 'education_consultant';
                if (user?.role !== expectedRole) {
                    const roleName = selectedRole === 'teacher' ? 'Öğretmen' : 'Danışman';
                    const actualRoleName = user?.role === 'teacher' ? 'Öğretmen' : 'Danışman';
                    setError(`${roleName} olarak giriş yapmaya çalıştınız ancak hesabınız ${actualRoleName} hesabıdır. Lütfen doğru rolü seçin.`);
                    turnstileTazele();
                    setLoading(false);
                    return;
                }

                onLogin(user);
            } else {
                setError('E-posta veya şifre hatalı!');
                turnstileTazele();
            }
        } catch (err) {
            setError('Giriş yapılırken bir hata oluştu.');
            turnstileTazele();
        } finally {
            setLoading(false);
        }
    };

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);

        try {
            let newUser: any = {
                id: Date.now().toString(),
                name,
                email,
                password,
                phoneNumber: phone,
                approvalStatus: 'pending'
            };

            if (selectedRole === 'teacher') {
                newUser = {
                    ...newUser,
                    role: 'teacher',
                    branch: branch,
                    appointments: [],
                    blockedSlots: []
                };
                await api.createTeacher(newUser);
            } else if (selectedRole === 'consultant') {
                newUser = {
                    ...newUser,
                    role: 'education_consultant',
                    studentIds: [],
                    appointments: [],
                    blockedSlots: []
                };
                await api.createConsultant(newUser);
            }

            setSuccess('Kayıt başarılı! Yönetici onayından sonra giriş yapabilirsiniz.');
            setTimeout(() => {
                setMode('login');
                setSuccess('');
            }, 3000);
        } catch (err) {
            setError('Kayıt sırasında bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSuccess = (user: any, isNewUser: boolean) => {
        if (user?.role !== 'teacher' && user?.role !== 'education_consultant') {
            setError('Bu sayfa sadece öğretmen ve danışmanlar içindir.');
            return;
        }
        if (isNewUser) {
            alert(`Hoş geldiniz ${user?.name}! Hesabınız yönetici onayına gönderildi.`);
        }
        onLogin(user);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 flex items-center justify-center p-4">
            {/* Background decoration */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-0 left-0 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"></div>
            </div>

            <div className="relative z-10 w-full max-w-2xl">
                <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl shadow-2xl overflow-hidden">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-purple-600/40 to-indigo-600/40 p-8 text-center">
                        <div className="w-20 h-20 mx-auto bg-gradient-to-br from-purple-500 to-indigo-500 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
                            <span className="text-4xl">👨‍🏫</span>
                        </div>
                        <h1 className="text-3xl font-bold text-white mb-2">Eğitmen Girişi</h1>
                        <p className="text-purple-200/70 text-sm">
                            {mode === 'login' ? 'Öğretmen ve Danışman Portalı' : 'Yeni Eğitmen Kaydı'}
                        </p>
                        
                    </div>

                    {/* Content */}
                    <div className="p-8">
                        {mode === 'login' ? (
                            <form onSubmit={handleLogin} className="space-y-4">
                                {/* Role Selection - Now at the top for all login types */}
                                <div>
                                    <label className="block text-sm font-medium text-white/80 mb-2">Giriş Yapılacak Rol</label>
                                    <div className="grid grid-cols-2 gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setSelectedRole('teacher')}
                                            className={`flex-1 py-3 px-3 rounded-xl font-medium transition-all ${selectedRole === 'teacher' ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg' : 'bg-white/10 text-white/70 hover:bg-white/20'}`}
                                        >
                                            📚 Öğretmen
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setSelectedRole('consultant')}
                                            className={`flex-1 py-3 px-3 rounded-xl font-medium transition-all ${selectedRole === 'consultant' ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg' : 'bg-white/10 text-white/70 hover:bg-white/20'}`}
                                        >
                                            👨‍🏫 Danışman
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-white/80 mb-1">E-posta</label>
                                    <input
                                        type="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all"
                                        placeholder="egitmen@supervisoregitim.com"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-white/80 mb-1">Şifre</label>
                                    <input
                                        type="password"
                                        required
                                        autoComplete="current-password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all"
                                        placeholder="••••••••"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowForgotPassword(true)}
                                        className="text-sm text-purple-300 hover:text-purple-200 mt-2 block w-full text-right transition-colors"
                                    >
                                        Şifremi Unuttum
                                    </button>
                                </div>

                                {error && (
                                    <div className="bg-red-500/20 border border-red-500/30 text-red-200 px-4 py-2 rounded-lg text-sm">
                                        {error}
                                    </div>
                                )}

                                <TurnstileWidget
                                    resetKey={turnstileSifirla}
                                    onVerify={(token) => setTurnstileToken(token)}
                                    onExpire={() => setTurnstileToken('')}
                                />

                                <button
                                    type="submit"
                                    disabled={loading || (turnstileEnabled && !turnstileToken)}
                                    className="w-full py-3 bg-gradient-to-r from-purple-500 to-indigo-500 text-white font-bold rounded-xl hover:from-purple-600 hover:to-indigo-600 transition-all shadow-lg disabled:opacity-50"
                                >
                                    {loading ? 'Giriş Yapılıyor...' : `${selectedRole === 'teacher' ? 'Öğretmen' : 'Danışman'} Olarak Giriş Yap`}
                                </button>

                                {/* Google Sign-In */}
                                <div className="flex items-center gap-3 my-4">
                                    <div className="h-px bg-white/20 flex-1"></div>
                                    <span className="text-white/50 text-sm">veya</span>
                                    <div className="h-px bg-white/20 flex-1"></div>
                                </div>

                                <GoogleSignInButton
                                    role={selectedRole}
                                    action="login"
                                    onSuccess={handleGoogleSuccess}
                                    onError={(err) => setError(err)}
                                />
                            </form>
                        ) : (
                            <form onSubmit={handleRegister} className="space-y-4">
                                {/* Role Selection */}
                                <div>
                                    <label className="block text-sm font-medium text-white/80 mb-2">Rolünüz</label>
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setSelectedRole('teacher')}
                                            className={`flex-1 py-3 rounded-xl font-medium transition-all ${selectedRole === 'teacher' ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white' : 'bg-white/10 text-white/70 hover:bg-white/20'}`}
                                        >
                                            📚 Öğretmen
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setSelectedRole('consultant')}
                                            className={`flex-1 py-3 rounded-xl font-medium transition-all ${selectedRole === 'consultant' ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white' : 'bg-white/10 text-white/70 hover:bg-white/20'}`}
                                        >
                                            👨‍🏫 Danışman
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-white/80 mb-1">Ad Soyad</label>
                                    <input
                                        type="text"
                                        required
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all"
                                        placeholder="Adınız Soyadınız"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-white/80 mb-1">E-posta</label>
                                    <input
                                        type="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all"
                                        placeholder="egitmen@supervisoregitim.com"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-white/80 mb-1">Şifre</label>
                                    <input
                                        type="password"
                                        required
                                        autoComplete="new-password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all"
                                        placeholder="••••••••"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-white/80 mb-1">Telefon</label>
                                    <input
                                        type="tel"
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all"
                                        placeholder="05XX XXX XX XX"
                                    />
                                </div>

                                {selectedRole === 'teacher' && (
                                    <div>
                                        <label className="block text-sm font-medium text-white/80 mb-1">Branş</label>
                                        <select
                                            value={branch}
                                            onChange={(e) => setBranch(e.target.value)}
                                            title="Branş seçimi"
                                            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all"
                                        >
                                            <option value="Matematik" className="bg-slate-800">Matematik</option>
                                            <option value="Fizik" className="bg-slate-800">Fizik</option>
                                            <option value="Kimya" className="bg-slate-800">Kimya</option>
                                            <option value="Biyoloji" className="bg-slate-800">Biyoloji</option>
                                            <option value="Türkçe" className="bg-slate-800">Türkçe</option>
                                            <option value="Edebiyat" className="bg-slate-800">Edebiyat</option>
                                            <option value="Tarih" className="bg-slate-800">Tarih</option>
                                            <option value="Coğrafya" className="bg-slate-800">Coğrafya</option>
                                            <option value="İngilizce" className="bg-slate-800">İngilizce</option>
                                        </select>
                                    </div>
                                )}


                                <div className="bg-amber-500/10 border border-amber-500/30 text-amber-200 px-4 py-3 rounded-lg text-sm">
                                    ⚠️ Kayıtlar yönetici onayı gerektirir.
                                </div>

                                {error && (
                                    <div className="bg-red-500/20 border border-red-500/30 text-red-200 px-4 py-2 rounded-lg text-sm">
                                        {error}
                                    </div>
                                )}

                                {success && (
                                    <div className="bg-emerald-500/20 border border-emerald-500/30 text-emerald-200 px-4 py-2 rounded-lg text-sm">
                                        {success}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all shadow-lg disabled:opacity-50"
                                >
                                    {loading ? 'Kayıt Yapılıyor...' : 'Kayıt Ol'}
                                </button>

                                {/* Google ile Kayıt Ol */}
                                <div className="flex items-center gap-3 my-4">
                                    <div className="h-px bg-white/20 flex-1"></div>
                                    <span className="text-white/50 text-sm">veya</span>
                                    <div className="h-px bg-white/20 flex-1"></div>
                                </div>

                                <GoogleSignInButtonCustom
                                    role={selectedRole}
                                    action="register"
                                    metadata={{
                                        branch: selectedRole === 'teacher' ? branch : undefined,
                                        phoneNumber: phone
                                    }}
                                    buttonText="Google ile Kayıt Ol"
                                    onSuccess={handleGoogleSuccess}
                                    onError={(err) => {
                                        if (!phone) {
                                            setError('Lütfen önce telefon numaranızı girin.');
                                            return;
                                        }
                                        setError(err);
                                    }}
                                    className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                                />
                            </form>
                        )}

                        {/* Toggle Mode */}
                        <div className="mt-6 text-center">
                            <button
                                onClick={() => {
                                    setMode(mode === 'login' ? 'register' : 'login');
                                    setError('');
                                    setSuccess('');
                                }}
                                className="text-purple-300 hover:text-purple-200 text-sm transition-colors"
                            >
                                {mode === 'login'
                                    ? 'Hesabınız yok mu? Kayıt olun'
                                    : 'Zaten hesabınız var mı? Giriş yapın'}
                            </button>
                        </div>

                        {/* Back to Home */}
                        <div className="mt-4 text-center">
                            <a
                                href="/"
                                className="text-white/50 hover:text-white/70 text-sm transition-colors"
                            >
                                ← Ana Sayfaya Dön
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <ForgotPasswordModal
                isOpen={showForgotPassword}
                onClose={() => setShowForgotPassword(false)}
            />
        </div>
    );
};

export default EducatorLoginPage;
